/**
 * Authentication Hooks (React Query)
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authApi } from "@/api/auth";
import { useUIStore } from "@/store/uiStore";
import { useAuthStore } from "@/store/authStore";
import { TokenStorage, endSession } from "@/api/client";
import type {
  ChangePasswordRequest,
  ChangePasswordResponse,
  PasswordResetRequest,
  PasswordResetConfirm,
  UpdateProfileRequest,
} from "@/types/auth";
import { toast } from "react-hot-toast";
import { broadcastAuthEvent } from "./useAuthSync";
import { clearSignOutReason, recordActivity } from "@/utils/idleSession";
import { getApiErrorMessage } from "@/utils/errors";

/**
 * Login mutation. ``toastErrors: false`` for a page that shows the error
 * next to the form (the login page): a toast too would announce it twice.
 */
export const useLogin = ({ toastErrors = true }: { toastErrors?: boolean } = {}) => {
  const { setUser } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authApi.login,
    onSuccess: (response, variables) => {
      const { user, tokens } = response.data;
      const { access_token, refresh_token, expires_in } = tokens;
      // A new session starts with an empty cache: nothing the previous user
      // of this browser looked at may be served to this one -- nor their
      // header campus (another school's, or one a super user can't clear).
      queryClient.clear();
      useUIStore.getState().setSelectedCampusId(null);
      // The server makes an unticked login a browser-session cookie; here it
      // decides whether the idle sign-out applies. A fresh login is activity.
      recordActivity();
      clearSignOutReason();
      TokenStorage.setRememberMe(variables.remember_me !== false);
      TokenStorage.setTokens(access_token, refresh_token, expires_in);
      setUser(user);
      // Broadcast login to other tabs
      broadcastAuthEvent("login", { userId: user.id, tenantId: user.tenant_id });
      toast.success(`Welcome back, ${user.first_name}!`);
    },
    onError: (error: unknown) => {
      if (!toastErrors) return;
      const message = getApiErrorMessage(error, "Login failed");
      toast.error(message);
    },
  });
};

/**
 * Logout mutation
 */
export const useLogout = () => {
  const { logout } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      logout();
      queryClient.clear();
      // Broadcast logout to other tabs
      broadcastAuthEvent("logout");
      toast.success("Logged out successfully");
    },
    onError: () => {
      // Still logout locally even if API call fails
      logout();
      queryClient.clear();
      // Broadcast logout to other tabs
      broadcastAuthEvent("logout");
    },
  });
};

/**
 * Get current user profile
 */
export const useProfile = () => {
  const { isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const response = await authApi.getProfile();
      return response.data;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Update profile mutation
 */
export const useUpdateProfile = () => {
  const { setUser } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfileRequest) => authApi.updateProfile(data),
    onSuccess: (response) => {
      setUser(response.data);
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("Profile updated successfully");
    },
    onError: (error: unknown) => {
      const message = getApiErrorMessage(error, "Failed to update profile");
      toast.error(message);
    },
  });
};

/**
 * Did a successful password change hand this browser its new login?
 * (authApi.changePassword has already stored it.)
 */
export const isSignedInAfterPasswordChange = (
  data: ChangePasswordResponse | undefined,
): boolean => !!data?.tokens?.access_token;

/**
 * The password changed but no new login came back: every older login is
 * revoked, so sign out now with a clear message rather than let the next
 * refresh do it unexplained.
 */
export const signOutAfterPasswordChange = (): void => {
  endSession();
  toast.success("Password changed. Please sign in again with your new password.");
};

/**
 * Change password mutation
 */
export const useChangePassword = ({ toastErrors = true }: { toastErrors?: boolean } = {}) => {
  return useMutation({
    mutationFn: (data: ChangePasswordRequest) => authApi.changePassword(data),
    onSuccess: (response) => {
      if (isSignedInAfterPasswordChange(response.data)) {
        toast.success("Password changed successfully");
      } else {
        signOutAfterPasswordChange();
      }
    },
    onError: (error: unknown) => {
      // Pages that show the error next to the form opt out of the toast.
      if (!toastErrors) return;
      const message = getApiErrorMessage(error, "Failed to change password");
      toast.error(message);
    },
  });
};

/**
 * Forgot password mutation (``toastErrors`` as for useLogin).
 */
export const useForgotPassword = ({ toastErrors = true }: { toastErrors?: boolean } = {}) => {
  return useMutation({
    mutationFn: (data: PasswordResetRequest) => authApi.requestPasswordReset(data),
    onSuccess: () => {
      toast.success("Password reset email sent. Please check your inbox.");
    },
    onError: (error: unknown) => {
      if (!toastErrors) return;
      const message = getApiErrorMessage(error, "Failed to send reset email");
      toast.error(message);
    },
  });
};

/**
 * Reset password mutation
 */
export const useResetPassword = () => {
  return useMutation({
    mutationFn: (data: PasswordResetConfirm) => authApi.confirmPasswordReset(data),
    onSuccess: () => {
      toast.success("Password reset successfully. You can now login.");
    },
    onError: (error: unknown) => {
      const message = getApiErrorMessage(error, "Failed to reset password");
      toast.error(message);
    },
  });
};
